import { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Table,
} from 'react-bootstrap';
import CaresPageHeader from '../../../shell/cares-page-header';
import type { AdminSection } from '../../config';
import {
  ClosedMenu,
  DefaultMenu,
  DetachedFn,
  DoubletFn,
  IconText,
  Reset,
  ThemeBackgroundColor,
  ThemePrimaryColor,
  iconOverlayFn,
  setHeaderPosition,
  setHeaderStyle,
  setLayout,
  setMenuPosition,
  setMenuStyle,
  setPageStyle,
  setPrimaryColor,
  updateBackgroundColor,
  updateLayout,
  updateNavStyle,
  updateTheme,
} from '../../../../../shared/data/switcherdata/switcherdata';
import { data$, getState, setState } from '../../../../../shared/services/switcherServices';
import './theme-options-page.scss';

type AdminSystemSettingsPageProps = {
  section: AdminSection;
  ownerEmail: string | undefined;
};

const primaryPresets = [
  { label: 'Violet', value: '171, 125, 241', swatch: 'rgb(171, 125, 241)' },
  { label: 'Sky', value: '69, 184, 224', swatch: 'rgb(69, 184, 224)' },
  { label: 'Rose', value: '231, 105, 182', swatch: 'rgb(231, 105, 182)' },
  { label: 'Evergreen', value: '26, 139, 114', swatch: 'rgb(26, 139, 114)' },
  { label: 'Ocean', value: '10, 106, 169', swatch: 'rgb(10, 106, 169)' },
] as const;

const backgroundPresets = [
  { label: 'Plum Night', value1: '70, 40, 115', value2: '84, 54, 129', swatch: 'rgb(84, 54, 129)' },
  { label: 'Slate Teal', value1: '32, 87, 105', value2: '46, 101, 119', swatch: 'rgb(46, 101, 119)' },
  { label: 'Mulberry', value1: '105, 40, 83', value2: '119, 54, 97', swatch: 'rgb(119, 54, 97)' },
  { label: 'Forest', value1: '0, 56, 51', value2: '14, 70, 65', swatch: 'rgb(14, 70, 65)' },
  { label: 'Midnight', value1: '5, 53, 100', value2: '19, 67, 114', swatch: 'rgb(19, 67, 114)' },
] as const;

const stateRows = [
  { label: 'Theme mode', key: 'dataThemeMode' },
  { label: 'Header style', key: 'dataHeaderStyles' },
  { label: 'Menu style', key: 'dataMenuStyles' },
  { label: 'Nav layout', key: 'dataNavLayout' },
  { label: 'Nav style', key: 'dataNavStyle' },
  { label: 'Page style', key: 'dataPageStyle' },
  { label: 'Width', key: 'dataWidth' },
  { label: 'Menu position', key: 'dataMenuPosition' },
  { label: 'Header position', key: 'dataHeaderPosition' },
  { label: 'Primary RGB', key: 'colorPrimaryRgb' },
  { label: 'Body background', key: 'bodyBg' },
  { label: 'Body background 2', key: 'bodyBg2' },
] as const;

const buildThemeDefaultsPayload = (themeState: ReturnType<typeof getState>) => ({
  dataThemeMode: themeState.dataThemeMode,
  dataHeaderStyles: themeState.dataHeaderStyles,
  dataMenuStyles: themeState.dataMenuStyles,
  dataNavLayout: themeState.dataNavLayout,
  dataVerticalStyle: themeState.dataVerticalStyle,
  toggled: themeState.toggled,
  dataNavStyle: themeState.dataNavStyle,
  dataPageStyle: themeState.dataPageStyle,
  dataWidth: themeState.dataWidth,
  dataMenuPosition: themeState.dataMenuPosition,
  dataHeaderPosition: themeState.dataHeaderPosition,
  colorPrimaryRgb: themeState.colorPrimaryRgb,
  bodyBg: themeState.bodyBg,
  bodyBg2: themeState.bodyBg2,
  inputBorder: themeState.inputBorder,
  lightRgb: themeState.lightRgb,
  formControlBg: themeState.formControlBg,
  gray: themeState.gray,
});

const AdminSystemSettingsPage: FC<AdminSystemSettingsPageProps> = ({ section }) => {
  const [themeState, setThemeState] = useState(getState());
  const [loadingSavedDefaults, setLoadingSavedDefaults] = useState(true);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [savedDefaults, setSavedDefaults] = useState<Record<string, string>>({});
  const [savedDefaultsUpdatedAt, setSavedDefaultsUpdatedAt] = useState<string | null>(null);
  const [savedDefaultsUpdatedBy, setSavedDefaultsUpdatedBy] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState<string | null>(null);

  useEffect(() => {
    const subscription = data$.subscribe((state) => {
      setThemeState(state);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSavedDefaults = async () => {
      setLoadingSavedDefaults(true);

      try {
        const response = await fetch('/api/v1/secure/admin/system-settings/theme-options', {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Unable to load theme defaults (${response.status}).`);
        }

        const payload = (await response.json()) as {
          settings: Record<string, string>;
          updated_at: string | null;
          updated_by: { email: string } | null;
        };

        if (!cancelled) {
          setSavedDefaults(payload.settings ?? {});
          setSavedDefaultsUpdatedAt(payload.updated_at);
          setSavedDefaultsUpdatedBy(payload.updated_by?.email ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          setRequestError(error instanceof Error ? error.message : 'Unable to load theme defaults.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSavedDefaults(false);
        }
      }
    };

    void loadSavedDefaults();

    return () => {
      cancelled = true;
    };
  }, []);

  const activeVerticalMode =
    themeState.dataVerticalStyle === 'closed'
      ? 'closed'
      : themeState.dataVerticalStyle === 'doublemenu'
        ? 'doublemenu'
        : themeState.toggled === 'icon-text-close'
          ? 'icontext'
          : themeState.toggled === 'icon-overlay-close'
            ? 'iconoverlay'
            : themeState.toggled === 'detached-close'
              ? 'detached'
              : 'default';

  return (
    <>
      <CaresPageHeader homepage="CARES Administration" activepage="Admin" page={section.navLabel} />

      <div className="theme-options-page">
              <Alert variant="info" className="custom-alert-icon shadow-none">
                Use this page for global admin shell presentation. Tenant-facing settings still
                belong in customer configuration flows, not in platform system settings.
              </Alert>
              {requestError ? (
                <Alert variant="danger" className="shadow-none">
                  {requestError}
                </Alert>
              ) : null}
              {requestSuccess ? (
                <Alert variant="success" className="shadow-none">
                  {requestSuccess}
                </Alert>
              ) : null}

              <Row className="g-4">
                <Col xxl={7}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Mode And Layout</div>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column gap-4">
                      <div>
                        <p id="theme-options-theme-mode-label" className="fw-semibold mb-3">Theme mode</p>
                        <div
                          aria-labelledby="theme-options-theme-mode-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          <Form.Check
                            type="radio"
                            id="admin-theme-light"
                            label="Light"
                            checked={themeState.dataThemeMode === 'light'}
                            onChange={() => updateTheme('light', 'clicked')}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-theme-dark"
                            label="Dark"
                            checked={themeState.dataThemeMode === 'dark'}
                            onChange={() => updateTheme('dark', 'clicked')}
                          />
                        </div>
                      </div>

                      <div>
                        <p id="theme-options-layout-label" className="fw-semibold mb-3">Navigation layout</p>
                        <div
                          aria-labelledby="theme-options-layout-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          <Form.Check
                            type="radio"
                            id="admin-layout-vertical"
                            label="Vertical"
                            checked={themeState.dataNavLayout === 'vertical'}
                            onChange={() => updateLayout('vertical')}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-layout-horizontal"
                            label="Horizontal"
                            checked={themeState.dataNavLayout === 'horizontal'}
                            onChange={() => updateLayout('horizontal')}
                          />
                        </div>
                      </div>

                      <div>
                        <p id="theme-options-page-style-label" className="fw-semibold mb-3">Page style</p>
                        <div
                          aria-labelledby="theme-options-page-style-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          {['regular', 'classic', 'modern'].map((style) => (
                            <Form.Check
                              key={style}
                              type="radio"
                              id={`admin-page-style-${style}`}
                              label={style.charAt(0).toUpperCase() + style.slice(1)}
                              checked={themeState.dataPageStyle === style}
                              onChange={() => setPageStyle(style as 'regular' | 'classic' | 'modern')}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p id="theme-options-width-label" className="fw-semibold mb-3">Width</p>
                        <div
                          aria-labelledby="theme-options-width-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          <Form.Check
                            type="radio"
                            id="admin-width-full"
                            label="Full width"
                            checked={themeState.dataWidth === 'fullwidth'}
                            onChange={() => setLayout('fullwidth')}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-width-boxed"
                            label="Boxed"
                            checked={themeState.dataWidth === 'boxed'}
                            onChange={() => setLayout('boxed')}
                          />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={5}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Operational Notes</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="px-0">
                          Theme changes reuse the same switcher state as the header switcher.
                        </ListGroup.Item>
                        <ListGroup.Item className="px-0">
                          Background presets force dark mode so contrast stays coherent.
                        </ListGroup.Item>
                        <ListGroup.Item className="px-0">
                          Saved defaults are bootstrapped into every shell session; regular users
                          only retain the top light/dark toggle.
                        </ListGroup.Item>
                      </ListGroup>
                      <div className="theme-options-page__actions mt-4 d-flex flex-column gap-2">
                        <Button
                          variant="primary"
                          className="theme-options-page__action-button"
                          disabled={savingDefaults}
                          onClick={async () => {
                            setSavingDefaults(true);
                            setRequestError(null);
                            setRequestSuccess(null);

                            try {
                              const response = await fetch('/api/v1/secure/admin/system-settings/theme-options', {
                                method: 'PATCH',
                                credentials: 'same-origin',
                                headers: {
                                  Accept: 'application/json',
                                  'Content-Type': 'application/json',
                                  'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '',
                                },
                                body: JSON.stringify(buildThemeDefaultsPayload(themeState)),
                              });

                              const payload = (await response.json()) as {
                                settings: Record<string, string>;
                                updated_at: string | null;
                                updated_by: { email: string } | null;
                                message?: string;
                                errors?: Record<string, string[]>;
                              };

                              if (!response.ok) {
                                const validationMessages = payload.errors
                                  ? Object.values(payload.errors).flat().join(' ')
                                  : '';
                                throw new Error(validationMessages || payload.message || `Unable to save theme defaults (${response.status}).`);
                              }

                              window.__CARES_BOOTSTRAP__ = {
                                ...window.__CARES_BOOTSTRAP__,
                                themeDefaults: payload.settings,
                              };
                              setSavedDefaults(payload.settings ?? {});
                              setSavedDefaultsUpdatedAt(payload.updated_at);
                              setSavedDefaultsUpdatedBy(payload.updated_by?.email ?? null);
                              setRequestSuccess('System-wide theme defaults saved.');
                            } catch (error) {
                              setRequestError(error instanceof Error ? error.message : 'Unable to save theme defaults.');
                            } finally {
                              setSavingDefaults(false);
                            }
                          }}
                        >
                          {savingDefaults ? 'Saving defaults...' : 'Save Current Theme As System Default'}
                        </Button>
                        <Button
                          variant="outline-primary"
                          className="theme-options-page__action-button"
                          disabled={loadingSavedDefaults || Object.keys(savedDefaults).length === 0}
                          onClick={() => {
                            setState(savedDefaults);
                            setRequestError(null);
                            setRequestSuccess('Saved system defaults reapplied to the current shell.');
                          }}
                        >
                          Reapply Saved Default
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="theme-options-page__action-button"
                          onClick={() => {
                            Reset();
                            setRequestError(null);
                            setRequestSuccess('Local shell reset to app defaults. Save again if you want to replace the stored system default.');
                          }}
                        >
                          Reset Local Shell
                        </Button>
                        <div className="fs-12 text-muted">
                          Saved default:
                          {' '}
                          {savedDefaultsUpdatedAt ? new Date(savedDefaultsUpdatedAt).toLocaleString() : 'Not set'}
                          {savedDefaultsUpdatedBy ? ` by ${savedDefaultsUpdatedBy}` : ''}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={6}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Navigation Presentation</div>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column gap-4">
                      <div>
                        <p id="theme-options-vertical-mode-label" className="fw-semibold mb-3">Vertical menu mode</p>
                        <div
                          aria-labelledby="theme-options-vertical-mode-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          <Form.Check
                            type="radio"
                            id="admin-vertical-default"
                            label="Default"
                            checked={activeVerticalMode === 'default'}
                            onChange={() => DefaultMenu()}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-vertical-closed"
                            label="Closed"
                            checked={activeVerticalMode === 'closed'}
                            onChange={() => ClosedMenu()}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-vertical-icontext"
                            label="Icon Text"
                            checked={activeVerticalMode === 'icontext'}
                            onChange={() => IconText()}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-vertical-overlay"
                            label="Icon Overlay"
                            checked={activeVerticalMode === 'iconoverlay'}
                            onChange={() => iconOverlayFn()}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-vertical-detached"
                            label="Detached"
                            checked={activeVerticalMode === 'detached'}
                            onChange={() => DetachedFn()}
                          />
                          <Form.Check
                            type="radio"
                            id="admin-vertical-double"
                            label="Double Menu"
                            checked={activeVerticalMode === 'doublemenu'}
                            onChange={() => DoubletFn()}
                          />
                        </div>
                      </div>

                      <div>
                        <p id="theme-options-menu-behavior-label" className="fw-semibold mb-3">Menu behavior</p>
                        <div
                          aria-labelledby="theme-options-menu-behavior-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          {[
                            { label: 'Menu Click', value: 'menu-click', toggled: 'menu-click-closed' },
                            { label: 'Menu Hover', value: 'menu-hover', toggled: 'menu-hover-closed' },
                            { label: 'Icon Click', value: 'icon-click', toggled: 'icon-click-closed' },
                            { label: 'Icon Hover', value: 'icon-hover', toggled: 'icon-hover-closed' },
                          ].map((option) => (
                            <Form.Check
                              key={option.value}
                              type="radio"
                              id={`admin-nav-style-${option.value}`}
                              label={option.label}
                              checked={themeState.dataNavStyle === option.value}
                              onChange={() => updateNavStyle(option.value, option.toggled)}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="fw-semibold mb-3">Pinned positions</p>
                        <div className="theme-options-page__pin-grid d-flex flex-wrap gap-4">
                          <div>
                            <span id="theme-options-menu-position-label" className="d-block fs-12 text-muted mb-2">Menu</span>
                            <div
                              aria-labelledby="theme-options-menu-position-label"
                              className="theme-options-page__option-group d-flex flex-wrap gap-3"
                              role="radiogroup"
                            >
                              <Form.Check
                                type="radio"
                                id="admin-menu-fixed"
                                label="Fixed"
                                checked={themeState.dataMenuPosition === 'fixed'}
                                onChange={() => setMenuPosition('fixed')}
                              />
                              <Form.Check
                                type="radio"
                                id="admin-menu-scrollable"
                                label="Scrollable"
                                checked={themeState.dataMenuPosition === 'scrollable'}
                                onChange={() => setMenuPosition('scrollable')}
                              />
                            </div>
                          </div>
                          <div>
                            <span id="theme-options-header-position-label" className="d-block fs-12 text-muted mb-2">Header</span>
                            <div
                              aria-labelledby="theme-options-header-position-label"
                              className="theme-options-page__option-group d-flex flex-wrap gap-3"
                              role="radiogroup"
                            >
                              <Form.Check
                                type="radio"
                                id="admin-header-fixed"
                                label="Fixed"
                                checked={themeState.dataHeaderPosition === 'fixed'}
                                onChange={() => setHeaderPosition('fixed')}
                              />
                              <Form.Check
                                type="radio"
                                id="admin-header-scrollable"
                                label="Scrollable"
                                checked={themeState.dataHeaderPosition === 'scrollable'}
                                onChange={() => setHeaderPosition('scrollable')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={6}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Theme Styling</div>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column gap-4">
                      <div>
                        <p id="theme-options-header-style-label" className="fw-semibold mb-3">Header style</p>
                        <div
                          aria-labelledby="theme-options-header-style-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          {['light', 'dark', 'color', 'gradient', 'transparent'].map((style) => (
                            <Form.Check
                              key={style}
                              type="radio"
                              id={`admin-header-style-${style}`}
                              label={style.charAt(0).toUpperCase() + style.slice(1)}
                              checked={themeState.dataHeaderStyles === style}
                              onChange={() => setHeaderStyle(style as 'light' | 'dark' | 'color' | 'gradient' | 'transparent')}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p id="theme-options-menu-style-label" className="fw-semibold mb-3">Menu style</p>
                        <div
                          aria-labelledby="theme-options-menu-style-label"
                          className="theme-options-page__option-group d-flex flex-wrap gap-3"
                          role="radiogroup"
                        >
                          {['light', 'dark', 'color', 'gradient', 'transparent'].map((style) => (
                            <Form.Check
                              key={style}
                              type="radio"
                              id={`admin-menu-style-${style}`}
                              label={style.charAt(0).toUpperCase() + style.slice(1)}
                              checked={themeState.dataMenuStyles === style}
                              onChange={() => setMenuStyle(style)}
                            />
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="fw-semibold mb-3">Primary accent presets</p>
                        <div className="theme-options-page__preset-group d-flex flex-wrap gap-2">
                          {primaryPresets.map((preset) => (
                            <Button
                              key={preset.value}
                              variant={themeState.colorPrimaryRgb === preset.value ? 'primary' : 'light'}
                              className="theme-options-page__preset-button d-flex align-items-center gap-2"
                              onClick={() => setPrimaryColor(preset.value)}
                            >
                              <span
                                className="rounded-circle border"
                                style={{
                                  width: '0.85rem',
                                  height: '0.85rem',
                                  backgroundColor: preset.swatch,
                                }}
                              />
                              {preset.label}
                            </Button>
                          ))}
                          <div className="theme-options-page__custom-picker d-flex align-items-center gap-2 ms-2">
                            <span className="fs-12 text-muted">Custom</span>
                            <ThemePrimaryColor ariaLabel="Custom primary accent color" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="fw-semibold mb-3">Background presets</p>
                        <div className="theme-options-page__preset-group d-flex flex-wrap gap-2">
                          {backgroundPresets.map((preset) => (
                            <Button
                              key={preset.value1}
                              variant={themeState.bodyBg === preset.value1 ? 'primary' : 'light'}
                              className="theme-options-page__preset-button d-flex align-items-center gap-2"
                              onClick={() => updateBackgroundColor(preset.value1, preset.value2, 'clicked')}
                            >
                              <span
                                className="rounded-circle border"
                                style={{
                                  width: '0.85rem',
                                  height: '0.85rem',
                                  backgroundColor: preset.swatch,
                                }}
                              />
                              {preset.label}
                            </Button>
                          ))}
                          <div className="theme-options-page__custom-picker d-flex align-items-center gap-2 ms-2">
                            <span className="fs-12 text-muted">Custom</span>
                            <ThemeBackgroundColor ariaLabel="Custom shell background color" />
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={12}>
                  <Card className="custom-card border shadow-none mb-0">
                    <Card.Header>
                      <div className="card-title">Live Theme State</div>
                    </Card.Header>
                    <Card.Body>
                      <Table responsive className="theme-options-page__state-table table mb-0">
                        <thead>
                          <tr>
                            <th>Setting</th>
                            <th>Current value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stateRows.map((row) => {
                            const value = themeState[row.key as keyof typeof themeState];
                            return (
                              <tr key={row.key}>
                                <td data-label="Setting">{row.label}</td>
                                <td data-label="Current value">{typeof value === 'string' && value !== '' ? value : 'Default'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
      </div>
    </>
  );
};

export default AdminSystemSettingsPage;
